import './IndividualMonth.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { ReactNode, useCallback, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  CompanyFiscalCal,
  getStartOfFiscalMonthFromInd,
  getStartOfFiscalMonthLabel,
  OUTSIDE_RANGE_ERR_MSG
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Placement } from '../../designConstants';
import { InteractiveDiv } from '../../InteractiveEl';
import { DateInfo, DateRange, MonthInfo } from '../date-picker-types';
import { FOCUS_OPTIONS, MONTHS } from '../DatePickerButton/DatePickerConstants';

const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface IndividualMonthProps {
  monthInfo: MonthInfo;
  onSelect: (date: string) => void;
  range?: Partial<DateRange>;
  focus?: (typeof FOCUS_OPTIONS)[number];
  setSelectedMonth: (selectedMonth: Date) => void;
  years: number[];
  maxDate?: string;
  classname?: string;
  fiscalCal: CompanyFiscalCal;
}

export const IndividualMonth = ({
  monthInfo,
  range,
  onSelect,
  focus,
  setSelectedMonth,
  years,
  maxDate,
  classname,
  fiscalCal
}: IndividualMonthProps) => {
  const dayGrid = useMemo(() => {
    const rows: (DateInfo | null)[][] = [];
    let row: (DateInfo | null)[] = [];
    let lastWasFirst = false;
    for (const dateInfo of monthInfo.dates) {
      if (dateInfo) {
        const info = { ...dateInfo };
        if (lastWasFirst) {
          // This is only necessary when we're on the same row as the start. Thus, if the row has no
          // length, meaning we're the first element, we don't need to set.
          if (row.length) {
            info.lastWasFirst = true;
          }
          lastWasFirst = false;
        }
        if (range) {
          if (info.date === range.start) {
            info.selectedLeft = true;
            lastWasFirst = true;
          } else if (info.date === range.end) {
            info.selectedRight = true;
            const last = row[row.length - 1];
            if (last) {
              last.nextIsLast = true;
            }
          } else if (range.start && range.end && info.date > range.start && info.date < range.end) {
            info.span = true;
          }
        }
        row.push(info);
      } else {
        row.push(null);
      }

      if (row.length === 7) {
        rows.push(row);
        row = [];
      }
    }
    return rows;
  }, [monthInfo, range]);

  const monthValue = useMemo(
    () => Dfns.getMonth(getStartOfFiscalMonthLabel(monthInfo.date, fiscalCal)),
    [monthInfo, fiscalCal]
  );

  const onMonthChange = useCallback(
    (monthStr: string) => {
      try {
        const newMonth = getStartOfFiscalMonthFromInd(
          monthInfo.year,
          parseInt(monthStr, 10),
          fiscalCal
        );
        setSelectedMonth(newMonth);
      } catch {
        console.error(OUTSIDE_RANGE_ERR_MSG);
      }
    },
    [setSelectedMonth, monthInfo, fiscalCal]
  );
  const onYearChange = useCallback(
    (year: string) => {
      try {
        const newMonth = getStartOfFiscalMonthFromInd(parseInt(year, 10), monthValue, fiscalCal);
        setSelectedMonth(newMonth);
      } catch {
        console.error(OUTSIDE_RANGE_ERR_MSG);
      }
    },
    [setSelectedMonth, monthValue, fiscalCal]
  );

  const dowLabels = useMemo(() => {
    const dayLabels: ReactNode[] = [];
    const sow = fiscalCal.startOfWeek || 0;
    for (let i = sow; i < DOW_LABELS.length + sow; i++) {
      dayLabels.push(<div key={`day-label-${i}`}>{DOW_LABELS[i % 7]}</div>);
    }
    return dayLabels;
  }, [fiscalCal]);

  return (
    <div className={cn('dp-month', classname)}>
      <div className='month-header'>
        <select value={monthValue} onChange={e => onMonthChange(e.target.value)}>
          {MONTHS.map((month, i) => (
            <option key={month} value={i}>
              {month}
            </option>
          ))}
        </select>
        <select value={monthInfo.year} onChange={e => onYearChange(e.target.value)}>
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className='days-of-week'>{dowLabels}</div>
      <div className='day-grid'>
        {dayGrid.map((row, i) => (
          <div key={`row-${i}`}>
            {row.map((day, j) => {
              const key = day ? day.date : `row-${i}-day-${j}`;
              const display = day ? day.display : '';
              const classes = cn({
                invalid: day?.invalid,
                'focus-start': day && focus === 'START',
                'focus-end': day && focus === 'END',
                focus: day && focus,
                selected: day?.selectedLeft || day?.selectedRight,
                'selected-left': day?.selectedLeft && range?.end && range?.end !== range?.start,
                'selected-right': day?.selectedRight && range?.start && range?.end !== range?.start,
                span: day?.span && !day?.selectedLeft && !day?.selectedRight,
                'last-was-first': day?.lastWasFirst,
                'next-is-last': day?.nextIsLast,
                'not-current-month': !day
              });

              return day && day.invalid && maxDate && day.date > maxDate ? (
                <OverlayTrigger
                  placement={Placement.LEFT}
                  overlay={<Tooltip id={day.date}>Data not yet available for these dates.</Tooltip>}
                  key={key}
                >
                  <InteractiveDiv
                    className={classes}
                    onClick={() => day && !day.invalid && onSelect(day.date)}
                  >
                    {display}
                  </InteractiveDiv>
                </OverlayTrigger>
              ) : (
                <InteractiveDiv
                  key={key}
                  className={classes}
                  onClick={() => day && !day.invalid && onSelect(day.date)}
                >
                  {display}
                </InteractiveDiv>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
